/* eslint-disable arrow-body-style */
import {inject} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {concatLatestFrom} from "@ngrx/operators";
import {Store} from "@ngrx/store";
import {MatSnackBar} from "@angular/material/snack-bar";
import {catchError, concatMap, distinctUntilChanged, filter, map, mergeMap, of, switchMap, throttleTime} from "rxjs";
import {tap} from "rxjs/operators";

import {ErrorHandlingUtil} from "../../shared/utils/error-handling-util";
import {AuthenticationService} from "../../shared/services/login/authentication.service";
import {FavoriteApiService} from "../generated-sources/api";
import {normalizeFavoriteUrl} from "../utils/favorite-url.util";
import {FavoriteActions} from "./favorite.actions";
import {selectFavorites} from "./favorite.selectors";

/**
 * Window during which a repeated star toggle is ignored. `delete` is optimistic while `create` is not,
 * so without this an accidental double-click after an optimistic remove would re-create the favorite
 * (losing its colour/note/position). Long enough to swallow a double-click, short enough to be invisible.
 */
const TOGGLE_THROTTLE_MS = 400;

/** Load the favorites once the user is authenticated (mirrors the notification init load). */
export const loadFavoritesOnAuth = createEffect(
    (authService = inject(AuthenticationService)) => {
        return authService.isAuthenticated$.pipe(
            distinctUntilChanged(),
            filter((authenticated) => authenticated),
            map(() => FavoriteActions.load())
        );
    },
    {functional: true}
);

export const loadFavorites = createEffect(
    (actions$ = inject(Actions), api = inject(FavoriteApiService)) => {
        return actions$.pipe(
            ofType(FavoriteActions.load),
            switchMap(() =>
                api.getFavorites().pipe(
                    map((favorites) => FavoriteActions.loadSuccess({favorites})),
                    catchError((err: any) =>
                        of(FavoriteActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const createFavorite = createEffect(
    (actions$ = inject(Actions), api = inject(FavoriteApiService)) => {
        return actions$.pipe(
            ofType(FavoriteActions.create),
            mergeMap(({request}) =>
                api.createFavorite(request).pipe(
                    map((favorite) => FavoriteActions.createSuccess({favorite})),
                    catchError((err: any) =>
                        of(FavoriteActions.createError({errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const updateFavorite = createEffect(
    (actions$ = inject(Actions), api = inject(FavoriteApiService)) => {
        return actions$.pipe(
            ofType(FavoriteActions.update),
            mergeMap(({id, request}) =>
                api.updateFavorite(id, request).pipe(
                    map((favorite) => FavoriteActions.updateSuccess({favorite})),
                    catchError((err: any) =>
                        of(FavoriteActions.updateError({id, errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const deleteFavorite = createEffect(
    (actions$ = inject(Actions), api = inject(FavoriteApiService)) => {
        return actions$.pipe(
            ofType(FavoriteActions.delete),
            mergeMap(({id}) =>
                api.deleteFavorite(id).pipe(
                    map(() => FavoriteActions.deleteSuccess({id})),
                    catchError((err: any) =>
                        of(FavoriteActions.deleteError({id, errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const reorderFavorites = createEffect(
    (actions$ = inject(Actions), api = inject(FavoriteApiService)) => {
        return actions$.pipe(
            ofType(FavoriteActions.reorder),
            // concatMap: persist each reorder in dispatch order. Using exhaustMap here would drop a
            // second drag made while the first PUT is in flight — the reducer applies it optimistically
            // but it would never reach the server, silently losing the reorder until the next reload.
            concatMap(({favoriteIds}) =>
                api.reorderFavorites({favoriteIds}).pipe(
                    map(() => FavoriteActions.reorderSuccess()),
                    catchError((err: any) =>
                        of(FavoriteActions.reorderError({errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

/**
 * Star-toggle resolver: if the current url is already favorited, remove it; otherwise create it.
 * Keeps the toggle decision in one place so the star component just emits intent.
 */
export const toggleCurrentFavorite = createEffect(
    (actions$ = inject(Actions), store$ = inject(Store)) => {
        return actions$.pipe(
            ofType(FavoriteActions.toggleCurrent),
            // Collapse an accidental double-click into a single toggle (leading edge keeps the first).
            throttleTime(TOGGLE_THROTTLE_MS),
            concatLatestFrom(() => store$.select(selectFavorites)),
            map(([{favType, url, label, color, note, contextSnapshot}, favorites]) => {
                const normalized = normalizeFavoriteUrl(url);
                const existing = favorites.find((f) => normalizeFavoriteUrl(f.url) === normalized);
                if (existing) {
                    return FavoriteActions.delete({id: existing.id});
                }
                return FavoriteActions.create({
                    request: {favType, url: normalized, label, color, note, contextSnapshot},
                });
            })
        );
    },
    {functional: true}
);

/**
 * Repair optimistic state after a failed mutation by reloading the canonical list. We deliberately
 * don't hand-roll a rollback for update/delete/reorder — a single reload is simpler and guaranteed
 * consistent with the server.
 */
export const reloadOnMutationError = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(
                FavoriteActions.updateError,
                FavoriteActions.deleteError,
                FavoriteActions.reorderError
            ),
            map(() => FavoriteActions.load())
        );
    },
    {functional: true}
);

export const showFavoriteMutationError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(
                FavoriteActions.createError,
                FavoriteActions.updateError,
                FavoriteActions.deleteError,
                FavoriteActions.reorderError
            ),
            tap(({errorMsg}) => snackBar.open(errorMsg || "Action failed.", "", {duration: 3000}))
        );
    },
    {functional: true, dispatch: false}
);
